import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { auth } from './auth';
import { getDocs, collection } from 'firebase/firestore';
import { db } from './firebase';

export default function IssueList({ issues, onEdit, onDelete, hideActions, hideProgressLink, showHeading, totalEstimatedCost, searchTerm, setSearchTerm, priorityFilter, setPriorityFilter, hideClosed, setHideClosed }) {
  // Only isSCUser is local state; all filter state is from props
  const [isSCUser, setIsSCUser] = useState(false);

  useEffect(() => {
    // Check if signed-in user is in SC User List
    const unsubscribe = auth.onAuthStateChanged(async user => {
      if (user && user.email) {
        const usersSnapshot = await getDocs(collection(db, 'users'));
        let found = false;
        usersSnapshot.forEach(doc => {
          const data = doc.data();
          if (data.email && data.email.toLowerCase() === user.email.toLowerCase()) {
            found = true;
          }
        });
        setIsSCUser(found);
      } else {
        setIsSCUser(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // Sort issues by dateReported (full ISO string) descending, latest on top
  const sortedIssues = [...issues].sort((a, b) => {
    const aDateStr = a.dateReported || '';
    const bDateStr = b.dateReported || '';
    return bDateStr.localeCompare(aDateStr);
  });
  // Use totalEstimatedCost from parent if provided
  // If not provided, fallback to local calculation (for single issue view)
  const computedTotalEstimatedCost = typeof totalEstimatedCost === 'number'
    ? totalEstimatedCost
    : issues.reduce((sum, issue) => {
        const val = parseFloat(issue.estimatedCost);
        return sum + (isNaN(val) ? 0 : val);
      }, 0);

  // Handle checkbox change for multi-select
  function handlePriorityChange(num) {
    setPriorityFilter(prev =>
      prev.includes(num)
        ? prev.filter(n => n !== num)
        : [...prev, num]
    );
  }

  function handleRegisterNewIssue(e) {
    if (!isSCUser) {
      e.preventDefault();
      alert('Only SC Users are allowed!');
      return;
    }
    if (typeof window !== 'undefined') {
      // This button is usually handled in parent, but if present here, you can add navigation logic
    }
  }

  function handleEdit(issue, e) {
    if (!isSCUser) {
      e.preventDefault();
      alert('Only SC Users are allowed!');
      return;
    }
    onEdit(issue);
  }

  function handleDelete(issue, e) {
    if (!isSCUser) {
      e.preventDefault();
      alert('Only SC Users are allowed!');
      return;
    }
    if(window.confirm('Are you sure you want to delete?')) onDelete(issue);
  }

  return (
    <div style={{ padding: 0, width: '100%', margin: '0 auto' }}>
      {showHeading && (
        <div style={{ marginBottom: '1.5rem', background: '#183a6d', color: 'white', padding: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.8rem' }}>
            <h2 style={{ margin: 0, color: 'white' }}>List of Issues</h2>
            <div style={{ marginLeft: 20, alignSelf: 'center' }}>
              {hideClosed ? (
                <a href="#" style={{ color: 'white', fontWeight: 500, marginRight: 8 }} onClick={e => { e.preventDefault(); setHideClosed(false); }}>Show All</a>
              ) : (
                <a href="#" style={{ color: 'white', fontWeight: 500, marginRight: 8 }} onClick={e => { e.preventDefault(); setHideClosed(true); }}>Hide Closed</a>
              )}
            </div>
            <div style={{ flex: 1 }} />
            <span style={{ fontWeight: 600, color: 'white', fontSize: '1.05em' }}>
              Total Estimated Cost: $
              {
                Number(computedTotalEstimatedCost).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})
              }
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', marginRight: 24 }}>
              <label style={{ marginRight: '0.5rem', fontWeight: 500, color: 'white' }}>Priorities:</label>
              {[1,2,3,4,5].map(num => (
                <label key={num} style={{ display: 'flex', alignItems: 'center', gap: '0.2rem', fontWeight: 400, color: 'white' }}>
                  <input
                    type="checkbox"
                    checked={priorityFilter.includes(num)}
                    onChange={() => handlePriorityChange(num)}
                    style={{ margin: 0 }}
                  />
                  {num}
                </label>
              ))}
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search issues..."
              style={{ padding: '0.4rem 0.8rem', borderRadius: 4, border: '1px solid #ccc', fontSize: '0.95em', width: '220px' }}
            />
          </div>
        </div>
      )}
      <div style={{ background: '#fff', borderRadius: 8, minHeight: 200, overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th>Date Reported</th>
              <th>By Whom</th>
              <th>Description</th>
              <th>Location</th>
              <th>Compliance Risk</th>
              <th>Priority</th>
              <th>Estimated Cost</th>
              <th>Fund</th>
              <th>Target Date</th>
              <th>Status</th>
              {!hideActions && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {sortedIssues.length === 0 ? (
              <tr><td colSpan={hideActions ? 10 : 11}>No issues found.</td></tr>
            ) : (
              sortedIssues.map((issue) => (
                <tr key={issue.id}>
                  <td style={{whiteSpace:'nowrap'}}>
                    {issue.dateReported ? (
                      <>
                        {new Date(issue.dateReported).toLocaleDateString()}<br />
                        <span style={{fontSize:'0.95em',color:'#888'}}>{new Date(issue.dateReported).toLocaleTimeString()}</span>
                      </>
                    ) : ''}
                  </td>
                  <td>{issue.byWhom}</td>
                  <td>
                    {issue.description}
                    {!hideProgressLink && (
                      <>
                        {' '}
                        <Link to={`/issues/${issue.id}/progress`} style={{ fontWeight: 600, color: '#1976d2', marginLeft: 8 }}>
                          See progress
                        </Link>
                      </>
                    )}
                  </td>
                  <td>{issue.location}</td>
                  <td>{issue.complianceRisk}</td>
                  <td>{issue.priority}</td>
                  <td>{issue.estimatedCost !== undefined && issue.estimatedCost !== '' ? `$${Number(issue.estimatedCost).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : ''}</td>
                  <td>{issue.fund}</td>
                  <td style={{whiteSpace:'nowrap'}}>{issue.targetQtr}</td>
                  <td>{issue.status}</td>
                  {!hideActions && (
                    <td>
                      <button 
                        onClick={e => handleEdit(issue, e)} 
                        style={{ padding: '2px 8px', fontSize: '0.8em', width: 56, marginRight: 4 }}
                      >Edit</button>
                      <button 
                        onClick={e => handleDelete(issue, e)} 
                        style={{ padding: '2px 8px', fontSize: '0.8em', width: 56 }}
                      >Delete</button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
